/**
 * HM Admin TalentTrain Workspace (read-only).
 *
 * Founder alignment 2026-08: certificates retired — this workspace surfaces
 * the Training Guide by Role, plan exports and recertification alerts only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Loader2, AlertTriangle, FileText } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

function StatBox({ label, value, testid }) {
  return (
    <Card className="p-4 shadow-card" data-testid={testid}>
      <p className="text-sm uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p className="text-2xl font-bold tabular-nums mt-1">{value}</p>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    released: 'bg-success/15 text-success border-success/30',
    issued: 'bg-success/15 text-success border-success/30',
  };
  return (
    <Badge className={`text-xs ${map[status] || 'bg-muted text-muted-foreground border-border'}`}>
      {status || '—'}
    </Badge>
  );
}

export default function AdminTalentTrainWorkspace({ companyId }) {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState({ items: [], count: 0 });
  const [matrix, setMatrix] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [ov, alertResp, matrixResp, reportResp] = await Promise.all([
        adminApi.ttAdminOverview(companyId),
        adminApi.ttAdminTrainingAlerts(companyId),
        adminApi.ttAdminGetRoleMatrix(companyId),
        adminApi.ttAdminListReports(companyId),
      ]);
      setOverview(ov);
      setAlerts(alertResp || { items: [], count: 0 });
      setMatrix(matrixResp?.items || []);
      setReports(reportResp?.items || []);
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [companyId, t]);

  useEffect(() => { load(); }, [load]);

  const alertBreakdown = useMemo(() => {
    const out = { red: 0, amber: 0 };
    for (const a of (alerts.items || [])) out[a.severity] = (out[a.severity] || 0) + 1;
    return out;
  }, [alerts]);

  if (loading || !overview) {
    return (
      <div className="py-10 text-center text-muted-foreground" data-testid="adm-tt-loading">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="adm-tt-workspace">
      {/* KPI strip — reuses overview aggregator (no new computation) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatBox label={t('adminTalentTrain.kpi.users')}
                 value={overview.users_total} testid="adm-tt-kpi-users" />
        <StatBox label={t('adminTalentTrain.kpi.withPlan')}
                 value={overview.collaborators_with_plan} testid="adm-tt-kpi-withplan" />
        <StatBox label={t('adminTalentTrain.kpi.actions')}
                 value={overview.actions_total} testid="adm-tt-kpi-actions" />
        <StatBox label={t('adminTalentTrain.kpi.completed')}
                 value={overview.actions_completed} testid="adm-tt-kpi-completed" />
        <StatBox label={t('adminTalentTrain.kpi.guidePositions')}
                 value={overview.matrix_positions} testid="adm-tt-kpi-guide" />
      </div>

      <Tabs defaultValue="guide" className="w-full">
        <TabsList data-testid="adm-tt-tabs">
          <TabsTrigger value="guide" data-testid="adm-tt-tab-guide">
            {t('adminTalentTrain.tabs.guide')} ({matrix.length})
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="adm-tt-tab-reports">
            {t('adminTalentTrain.tabs.reports')} ({reports.length})
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="adm-tt-tab-alerts">
            {t('adminTalentTrain.tabs.alerts')} ({alerts.count})
          </TabsTrigger>
        </TabsList>

        {/* Training Guide by Role (read-only) */}
        <TabsContent value="guide" className="mt-4" data-testid="adm-tt-panel-guide">
          <Card className="p-5 shadow-card">
            <p className="text-sm text-muted-foreground mb-4">
              {t('adminTalentTrain.guide.subtitle')}
            </p>
            {matrix.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t('adminTalentTrain.guide.empty')}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('clientTalentTrain.matrix.th.position')}</TableHead>
                    <TableHead>{t('clientTalentTrain.matrix.th.mandatory')}</TableHead>
                    <TableHead className="text-center">{t('clientTalentTrain.matrix.th.optional')}</TableHead>
                    <TableHead className="text-center">{t('clientTalentTrain.matrix.th.recerts')}</TableHead>
                    <TableHead className="text-center">{t('clientTalentTrain.matrix.th.internal')}</TableHead>
                    <TableHead className="text-center">{t('clientTalentTrain.matrix.th.external')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrix.map((row, i) => (
                    <TableRow key={row.id || i} data-testid={`adm-tt-guide-row-${i}`}>
                      <TableCell className="font-medium">{row.position}</TableCell>
                      <TableCell className="text-sm">
                        {(row.mandatory || []).slice(0, 4).map((m) => (
                          <Badge key={m} variant="outline" className="text-xs mr-1 mb-1">{m}</Badge>
                        ))}
                        {(row.mandatory || []).length > 4 && (
                          <span className="text-xs text-muted-foreground">+{(row.mandatory || []).length - 4}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm tabular-nums">{(row.optional || []).length}</TableCell>
                      <TableCell className="text-center text-sm tabular-nums">{(row.recerts || []).length}</TableCell>
                      <TableCell className="text-center text-sm tabular-nums">{(row.internal || []).length}</TableCell>
                      <TableCell className="text-center text-sm tabular-nums">{(row.external || []).length}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Reports */}
        <TabsContent value="reports" className="mt-4" data-testid="adm-tt-panel-reports">
          <Card className="p-5 shadow-card">
            {reports.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t('adminTalentTrain.reports.empty')}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.kind', { defaultValue: 'Kind' })}</TableHead>
                    <TableHead>{t('common.released', { defaultValue: 'Released' })}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="text-right">{t('common.report', { defaultValue: 'Report id' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((r, i) => (
                    <TableRow key={r.id} data-testid={`adm-tt-report-row-${i}`}>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                          {r.kind || 'report'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{(r.released_at || '').slice(0, 19).replace('T', ' ') || '—'}</TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell className="text-right text-xs font-mono text-muted-foreground">
                        {(r.id || '').slice(0, 8)}…
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Recertification alerts (sourced from training actions) */}
        <TabsContent value="alerts" className="mt-4" data-testid="adm-tt-panel-alerts">
          <Card className="p-5 shadow-card">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-sm">
                  {t('adminTalentTrain.alerts.title')}
                </h3>
                {alerts.count === 0 ? (
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('adminTalentTrain.alerts.empty')}
                  </p>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {alertBreakdown.red > 0 && (
                      <Badge className="bg-destructive/15 text-destructive border-destructive/30">
                        {t('clientTalentTrain.alerts.expired', { n: alertBreakdown.red })}
                      </Badge>
                    )}
                    {alertBreakdown.amber > 0 && (
                      <Badge className="bg-warning/15 text-warning border-warning/30">
                        {t('clientTalentTrain.alerts.expiringSoon', { n: alertBreakdown.amber })}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
            {alerts.items.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.severity', { defaultValue: 'Severity' })}</TableHead>
                    <TableHead>{t('common.training', { defaultValue: 'Training' })}</TableHead>
                    <TableHead>{t('tt.recertDueDate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.items.map((a, i) => (
                    <TableRow key={`${a.action_id}-${i}`} data-testid={`adm-tt-alert-row-${i}`}>
                      <TableCell>
                        <Badge className={
                          a.severity === 'red' ? 'bg-destructive/15 text-destructive border-destructive/30'
                            : 'bg-warning/15 text-warning border-warning/30'
                        }>
                          {a.severity === 'red' ? t('tt.recertState.expired') : t('tt.recertState.expiring')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{a.training_name || '—'}</TableCell>
                      <TableCell className="text-sm">{(a.due_date || a.expires_at || '').slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
